import React, { useState } from 'react';
import { Search, Filter, Calendar, Users, CheckCircle, TrendingUp, Wand2, Clock, AlertTriangle, FileText, BarChart2, Target } from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { useProposalStore } from '../stores/proposalStore';
import { ProposalStats } from '../components/proposals/ProposalStats';
import { ProposalList } from '../components/proposals/ProposalList';
import { AIProposalGenerator } from '../components/proposals/AIProposalGenerator';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

export function Proposals() {
  const navigate = useNavigate();
  const { openModal } = useUIStore();
  const { proposals } = useProposalStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('30');
  const [selectedView, setSelectedView] = useState<'list' | 'kanban'>('list');

  const stats = [
    {
      title: 'Active Proposals',
      value: '12',
      change: { value: '+2.5%', type: 'positive' },
      description: 'Current proposals',
      icon: Calendar,
      color: 'bg-purple-100 text-purple-600',
    },
    {
      title: 'Pending Reviews',
      value: '4',
      change: { value: '-8%', type: 'negative' },
      description: 'Awaiting review',
      icon: CheckCircle,
      color: 'bg-yellow-100 text-yellow-600',
    },
    {
      title: 'Team Members',
      value: '8',
      change: { value: '+3', type: 'positive' },
      description: 'Active contributors',
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      title: 'Success Rate',
      value: '68%',
      change: { value: '+4.2%', type: 'positive' },
      description: 'Win rate',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-600',
    },
  ];

  const insights = [
    {
      title: 'Upcoming Deadlines',
      value: '3',
      description: 'Due this week',
      icon: Calendar,
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      title: 'Compliance Score',
      value: '94%',
      description: 'Above target',
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      title: 'Team Workload',
      value: '85%',
      description: 'Resource utilization',
      icon: Users,
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Win Rate Trend',
      value: '+4.2%',
      description: 'Last 30 days',
      icon: TrendingUp,
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
  ];

  const recentActivity = [
    {
      id: '1',
      type: 'update',
      title: 'Technical Proposal Updated',
      description: 'Sarah Johnson updated the technical approach section',
      timestamp: '2 hours ago',
      user: {
        name: 'Sarah Johnson',
        avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson',
      },
    },
    {
      id: '2',
      type: 'comment',
      title: 'New Comment Added',
      description: 'Michael Chen commented on the cost estimation',
      timestamp: '4 hours ago',
      user: {
        name: 'Michael Chen',
        avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
      },
    },
  ];

  const upcomingDeadlines = [
    {
      id: '1',
      title: 'Technical Proposal Review',
      dueDate: '2024-03-15',
      assignee: {
        name: 'Sarah Johnson',
        avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson',
      },
      priority: 'high',
      progress: 75,
    },
    {
      id: '2',
      title: 'Cost Estimation Update',
      dueDate: '2024-03-18',
      assignee: {
        name: 'Michael Chen',
        avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
      },
      priority: 'medium',
      progress: 45,
    },
  ];

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      // Simulate export delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success(`Proposals exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export proposals');
    }
  };

  const handleBulkAction = (action: string) => {
    toast.success(`Bulk action "${action}" coming soon`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Proposals</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage and track your proposal pipeline efficiently
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowAIGenerator(true)}
            className="inline-flex items-center rounded-lg bg-purple-50 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-100"
          >
            <Wand2 className="mr-2 h-4 w-4" />
            AI Generator
          </button>
          <button
            onClick={() => openModal('newProposal')}
            className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-purple-700 hover:shadow-md"
          >
            Create New Proposal
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.title}
            className="group relative overflow-hidden rounded-lg bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="flex items-center">
              <div className={`rounded-lg ${stat.color} p-3`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.title}</p>
                <div className="mt-1">
                  <span className="text-2xl font-semibold text-gray-900">{stat.value}</span>
                  <span className={`ml-2 text-sm font-medium ${
                    stat.change.type === 'positive' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change.value}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">{stat.description}</p>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-purple-500 to-transparent opacity-0 transition-opacity group-hover:opacity-100"></div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search proposals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="rounded-lg border border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="all">All Proposals</option>
            <option value="draft">Drafts</option>
            <option value="review">In Review</option>
            <option value="submitted">Submitted</option>
          </select>
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="rounded-lg border border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <button className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            <Filter className="mr-2 h-4 w-4" />
            More Filters
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
        <div className="flex items-center space-x-4">
          <select
            className="rounded-lg border border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
            onChange={(e) => handleBulkAction(e.target.value)}
          >
            <option value="">Bulk Actions</option>
            <option value="archive">Archive Selected</option>
            <option value="share">Share Selected</option>
            <option value="delete">Delete Selected</option>
          </select>
          <button
            onClick={() => handleExport('pdf')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <FileText className="mr-2 h-4 w-4" />
            Export PDF
          </button>
          <button
            onClick={() => handleExport('excel')}
            className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <FileText className="mr-2 h-4 w-4" />
            Export Excel
          </button>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSelectedView('list')}
            className={`rounded-lg p-2 ${
              selectedView === 'list'
                ? 'bg-purple-100 text-purple-600'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-500'
            }`}
          >
            <FileText className="h-5 w-5" />
          </button>
          <button
            onClick={() => setSelectedView('kanban')}
            className={`rounded-lg p-2 ${
              selectedView === 'kanban'
                ? 'bg-purple-100 text-purple-600'
                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-500'
            }`}
          >
            <BarChart2 className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Insights Grid */}
      <div className="grid gap-6 lg:grid-cols-4">
        {insights.map((insight) => (
          <div
            key={insight.title}
            className={`rounded-lg ${insight.bgColor} p-6`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">{insight.title}</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{insight.value}</p>
                <p className="mt-1 text-sm text-gray-500">{insight.description}</p>
              </div>
              <div className={`rounded-lg bg-white/50 p-3 ${insight.iconColor}`}>
                <insight.icon className="h-6 w-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Recent Activity</h2>
          <button className="text-sm font-medium text-purple-600 hover:text-purple-500">
            View All
          </button>
        </div>
        <div className="mt-6 flow-root">
          <ul className="-mb-8">
            {recentActivity.map((activity, activityIdx) => (
              <li key={activity.id}>
                <div className="relative pb-8">
                  {activityIdx !== recentActivity.length - 1 ? (
                    <span
                      className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                      aria-hidden="true"
                    />
                  ) : null}
                  <div className="relative flex space-x-3">
                    <div>
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100">
                        <img
                          className="h-8 w-8 rounded-full"
                          src={activity.user.avatar}
                          alt=""
                        />
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                      <div>
                        <p className="text-sm text-gray-900">{activity.title}</p>
                        <p className="mt-0.5 text-sm text-gray-500">
                          {activity.description}
                        </p>
                      </div>
                      <div className="whitespace-nowrap text-right text-sm text-gray-500">
                        <time dateTime={activity.timestamp}>{activity.timestamp}</time>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Priority Tasks */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">Priority Tasks</h2>
          <button className="text-sm font-medium text-purple-600 hover:text-purple-500">
            View All
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {upcomingDeadlines.map((task) => (
            <div
              key={task.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={task.assignee.avatar}
                  alt={task.assignee.name}
                  className="h-10 w-10 rounded-full"
                />
                <div>
                  <h3 className="font-medium text-gray-900">{task.title}</h3>
                  <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>Due: {task.dueDate}</span>
                    <span>•</span>
                    <span>{task.assignee.name}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-20 rounded-full bg-gray-200">
                    <div
                      className="h-2 rounded-full bg-purple-600"
                      style={{ width: `${task.progress}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500">{task.progress}%</span>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  task.priority === 'high'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {task.priority} priority
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Generator Modal */}
      {showAIGenerator && (
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <AIProposalGenerator onComplete={() => setShowAIGenerator(false)} />
        </div>
      )}

      {/* Proposal List */}
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <ProposalList proposals={proposals} />
      </div>
    </div>
  );
}